type TranscriptionAPIResponse = Array<{
	chunks: Array<any>;
	id: string;
	input_tokens: number;
	seconds: number | null;
	text: string;
	total_tokens: number;
}>;

// https://hello.atlassian.net/wiki/spaces/CA3/pages/5173751347/Audio+Streaming+Endpoint+Tech+Spec
export const getTranscriptions = async ({
	audioString,
}: {
	audioString: string;
}): Promise<TranscriptionAPIResponse> => {
	const response = await fetch('/gateway/api/v1/audio/transcriptions', {
		method: 'POST',
		body: JSON.stringify({
			data: [{ audio: audioString }],
			model: 'whisper-1',
			audio_format: 'wav',
		}),
		headers: {
			'Content-Type': 'application/json',
			'Request-ID': crypto.randomUUID(),
		},
	});

	if (!response.ok) {
		throw new Error(`HTTP error! status: ${response.status}`);
	}

	if (!response.body) {
		throw new Error('Response body is null');
	}

	return response.json();
};

export type ConvoAIWebSocketServerMessage = {
	message: { content: string };
	millisOffset: number;
	type: string;
};

export class ConvoAIWebSocketAPI {
	private socket: WebSocket;

	constructor(
		cloudID: string,
		product: string,
		experience: string,
		intent: string,
		private readonly onMessage: (event: ConvoAIWebSocketServerMessage) => void,
	) {
		this.socket = new WebSocket(
			`/wss/${cloudID}/realtime?product=${product}&experience=${experience}&intent=${intent}`,
		);
		this.socket.addEventListener('message', this.onServerMessage);
	}

	updateTranscriptionConfig(
		instructions: string = '',
		model: string = 'gpt-4o-transcribe',
		noise_detection: string = 'near_field',
		threshold: number = 0.5,
		prefix_padding_ms: number = 300,
		silence_duration_ms: number = 500,
	): void {
		this.socket.send(
			JSON.stringify({
				type: 'rovo.transcriptions.update',
				instructions,
				model,
				noise_detection,
				threshold,
				prefix_padding_ms,
				silence_duration_ms,
			}),
		);
	}

	sendAudio(audio: string): void {
		this.socket.send(JSON.stringify({ audio, type: 'rovo.input_audio_buffer.append' }));
	}

	commitAudio(): void {
		this.socket.send(JSON.stringify({ type: 'rovo.input_audio_buffer.commit' }));
	}

	connect(): Promise<ConvoAIWebSocketAPI> {
		return new Promise((resolve, reject) => {
			this.socket.onopen = () => resolve(this);
			this.socket.onerror = (event) => reject(event);
		});
	}

	onServerMessage = (event: MessageEvent): void => {
		const data = JSON.parse(event.data) as ConvoAIWebSocketServerMessage;
		this.onMessage(data);
	};

	disconnect(): void {
		this.socket?.close();
	}

	static create(
		cloudID: string,
		product: string,
		experience: string,
		intent: string,
		onMessage: (event: ConvoAIWebSocketServerMessage) => void,
	): Promise<ConvoAIWebSocketAPI> {
		const api = new ConvoAIWebSocketAPI(cloudID, product, experience, intent, onMessage);
		return api.connect();
	}
}
